<template>
  <div class="container">
    <div class="space-y-2 mb-6">
      <h2 class="text-2xl font-semibold">Ceremony Intelligence</h2>
      <p class="text-gray-600">Search for standout moments within your ceremonies.</p>
    </div>

    <section class="panel">
      <ceremony-search-controls
        :allowed-universities="allowedUniversities"
        :selected-slug="selectedSlug"
        :ceremonies="ceremonies"
        :ceremonies-loading="ceremoniesLoading"
        :ceremony-error="ceremonyError"
        :selected-ceremony-id="selectedCeremonyId"
        :query-text="queryText"
        :can-submit-search="canSubmitSearch"
        :format-ceremony="formatCeremony"
        @update:selectedSlug="selectedSlug = $event"
        @update:selectedCeremonyId="selectedCeremonyId = $event"
        @update:queryText="queryText = $event"
        @university-change="onUniversityChange"
        @ceremony-change="onCeremonyChange"
        @submit-search="submitSearch"
      />
    </section>

    <section
      v-if="selectedCeremonyId && (ceremonyChapters.length || canBuildChapters || contextMessage || contextError)"
      class="panel"
    >
      <div class="flex items-center justify-between mb-3">
        <div>
          <h3 class="text-lg font-semibold">Chapters</h3>
          <p class="text-sm text-gray-600">Transcript-derived sections for navigating the ceremony.</p>
        </div>
        <button
          v-if="canBuildChapters"
          type="button"
          class="px-3 py-2 text-sm font-semibold text-white bg-gray-900 rounded disabled:opacity-50"
          :disabled="contextProcessing"
          @click="requestChapterBackfill"
        >
          {{ contextProcessing ? 'Starting…' : chapterActionLabel }}
        </button>
      </div>
      <p v-if="contextMessage" class="text-sm text-gray-600 mb-3">{{ contextMessage }}</p>
      <p v-if="contextError" class="text-sm text-red-600 mb-3">{{ contextError }}</p>
      <div v-if="ceremonyChapters.length" class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <button
          v-for="(chapter, index) in ceremonyChapters"
          :key="chapterEventId(chapter, index)"
          type="button"
          class="text-left border rounded px-3 py-2 hover:border-gray-500"
          :class="{ 'border-gray-900 bg-gray-50': chapterEventId(chapter, index) === selectedEventId }"
          @click="selectChapter(chapter, index)"
        >
          <span class="block text-sm font-semibold">{{ chapterTitle(chapter) }}</span>
          <span class="block text-xs text-gray-500">
            {{ formatDuration(chapter.start) }}–{{ formatDuration(chapter.end) }}
          </span>
        </button>
      </div>
    </section>

    <section 
      v-if="selectedCeremonyId && 
        (ceremonyHighlightsLoading || ceremonyHighlights.length)"
      class="panel"
    >
      <ceremony-highlights
        :highlights="ceremonyHighlights"
        :is-loading="ceremonyHighlightsLoading"
        :selected-event-id="selectedEventId"
        :active-video-url="activeVideoUrl"
        :format-duration="formatDuration"
        @select-highlight="selectHighlight"
      />
    </section>

    <section v-if="hasSubmittedSearch" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <aside class="panel lg:col-span-1">
        <ceremony-event-list
          :events="eventsData"
          :events-loading="eventsLoading"
          :selected-event-id="selectedEventId"
          :events-upload-error="eventsUploadError"
          :format-duration="formatDuration"
          @select-event="selectEvent"
        />
      </aside>

      <div class="panel lg:col-span-2">
        <ceremony-media-player
          ref="mediaPlayer"
          :active-video-url="activeVideoUrl"
          :ceremony-video-loading="ceremonyVideoLoading"
          :ceremony-video-error="ceremonyVideoError"
          :video-options="videoOptions"
          @loadeddata="onPlayerLoadedData"
        />

        <ceremony-timeline
          :events-loading="eventsLoading"
          :timeline-duration-seconds="timelineDurationSeconds"
          :clustered-timeline-markers="clusteredTimelineMarkers"
          :selected-timeline-group="selectedTimelineGroup"
          :expanded-timeline-range="expandedTimelineRange"
          :expanded-timeline-markers="expandedTimelineMarkers"
          :expanded-timeline-height-px="expandedTimelineHeightPx"
          :selected-event-id="selectedEventId"
          :active-video-url="activeVideoUrl"
          :format-duration="formatDuration"
          @select-group="selectTimelineMarker"
          @select-event="selectFocusedTimelineEvent"
        />

        <ceremony-event-details
          :active-event="activeEvent"
          :events-loading="eventsLoading"
          :event-feedback="eventFeedback[activeEvent.id] || ''"
          :feedback-submitting="Boolean(feedbackSubmitting[activeEvent.id])"
          :feedback-error="feedbackErrors[activeEvent.id] || ''"
          :format-duration="formatDuration"
          :export-state="eventExports[activeEvent.id] || {}"
          @submit-feedback="recordEventFeedback"
          @export-event="requestEventExport"
          @download-export="recordExportDownload"
        />
      </div>
    </section>

    <section v-else-if="isHighlightOnlyView" class="panel">
      <ceremony-highlights-player
        ref="mediaPlayer"
        :active-video-url="activeVideoUrl"
        :ceremony-video-loading="ceremonyVideoLoading"
        :ceremony-video-error="ceremonyVideoError"
        :video-options="videoOptions"
        :active-event="activeEvent"
        :format-duration="formatDuration"
        @loadeddata="onPlayerLoadedData"
      />
    </section>

    <ceremony-processing-controls
      v-if="isAdmin"
      :selected-slug="selectedSlug"
      :ceremonies="ceremonies"
      :format-ceremony="formatCeremony"
    />
  </div>
</template>

<script>
import axios from 'axios'
import 'video.js/dist/video-js.css'
import Auth from '../../api/auth.js'
import { trackAdminEvent } from '../../admin/activity.js'
import CeremonyEventDetails from './search/CeremonyEventDetails.vue'
import CeremonyEventList from './search/CeremonyEventList.vue'
import CeremonyHighlights from './search/CeremonyHighlights.vue'
import CeremonyMediaPlayer from './search/CeremonyMediaPlayer.vue'
import CeremonySearchControls from './search/CeremonySearchControls.vue'
import CeremonyProcessingControls from './search/CeremonyProcessingControls.vue'
import CeremonyTimeline from './search/CeremonyTimeline.vue'
import CeremonyHighlightsPlayer from './search/CeremonyHighlightsPlayer.vue'
import {
  SECTION_EXPORT_POLL_DEADLINE_MS,
  SECTION_EXPORT_POLL_INITIAL_MS,
  SECTION_EXPORT_POLL_MAX_MS,
  createSectionExport,
  getSectionExport,
  isSectionExportPending,
} from '../../api/sectionExports.js'

export default {
  name: 'CeremonySearch',
  components: {
    CeremonyEventDetails,
    CeremonyEventList,
    CeremonyHighlights,
    CeremonyHighlightsPlayer,
    CeremonyMediaPlayer,
    CeremonyProcessingControls,
    CeremonySearchControls,
    CeremonyTimeline,
  },
  data() {
    const payload = Auth.getPayload()
    const tokenUniversities = Array.isArray(payload?.universities) ? payload.universities : []
    const tokenRoles = Array.isArray(payload?.roles) ? payload.roles : []
    return {
      tokenUniversities,
      isAdmin: Boolean(payload?.admin || tokenRoles.includes('admin')),
      allowedUniversities: [],
      selectedSlug: tokenUniversities[0] || '',
      ceremonies: [],
      ceremoniesLoading: false,
      ceremonyError: '',
      selectedCeremonyId: '',
      ceremonyVideoLoading: false,
      ceremonyVideoError: '',
      ceremonyVideoUrl: '',
      ceremonyVideoType: '',
      ceremonyVideoDuration: 0,
      queryText: '',
      eventsUploadError: '',
      eventsLoading: false,
      eventsData: [],
      hasSubmittedSearch: false,
      selectedEventId: '',
      selectedTimelineGroupId: '',
      eventFeedback: {},
      feedbackSubmitting: {},
      feedbackErrors: {},
      currentSearchId: '',
      eventPlaybackTimer: null,
      exportPollTimers: {},
      eventExports: {},
      top_k: 20,
      ceremonyHighlights: [],
      ceremonyHighlightsLoading: false,
      ceremonyChapters: [],
      contextProcessing: false,
      contextMessage: '',
      contextError: '',
      contextPollTimer: null,
      contextPollDeadline: 0,
      ceremonyContextRequestId: 0,
    }
  },
  computed: {
    selectedUniversity() {
      return (this.allowedUniversities || []).find(u => u.slug === this.selectedSlug) || null
    },
    canSubmitSearch() {
      return Boolean(
        !this.eventsLoading &&
        String(this.selectedCeremonyId || '').trim() &&
        String(this.queryText || '').trim()
      )
    },
    activeVideoUrl() {
      return this.ceremonyVideoUrl
    },
    activeVideoType() {
      if (this.ceremonyVideoType) return this.ceremonyVideoType
      const url = String(this.activeVideoUrl || '').toLowerCase().split('?')[0]
      if (url.endsWith('.mp4')) return 'video/mp4'
      return 'application/x-mpegURL'
    },
    activeEvent() {
      return this.eventsData.find(event => event.id === this.selectedEventId)
        || this.highlightEventFromId(this.selectedEventId)
        || this.chapterEventFromId(this.selectedEventId)
        || this.eventsData[0]
        || {}
    },
    isHighlightOnlyView() {
      return !this.hasSubmittedSearch && Boolean(
        this.highlightEventFromId(this.selectedEventId)
        || this.chapterEventFromId(this.selectedEventId)
      )
    },
    canBuildChapters() {
      return Boolean(
        this.isAdmin
        && this.activeVideoUrl
        && !this.ceremonyHighlightsLoading
        && !this.contextMessage
      )
    },
    chapterActionLabel() {
      return this.ceremonyChapters.length ? 'Rebuild chapters' : 'Build chapters'
    },
    timelineDurationSeconds() {
      const eventSeconds = this.timelineEvents.map(event => event.seconds)
      const maxEventSeconds = eventSeconds.length ? Math.max(...eventSeconds) : 0
      return Math.max(this.ceremonyVideoDuration || 0, maxEventSeconds + 45, 20 * 60)
    },
    timelineEvents() {
      return this.eventsData
        .map(event => ({
          ...event,
          seconds: Number(event.windowStartSeconds ?? event.timestamp),
        }))
        .filter(event => Number.isFinite(event.seconds))
        .sort((a, b) => a.seconds - b.seconds)
    },
    clusteredTimelineMarkers() {
      const duration = this.timelineDurationSeconds || 1
      const thresholdSeconds = Math.max(12, Math.min(45, duration * 0.02))
      const maxGroupSpanSeconds = 120
      const groups = []

      for (const event of this.timelineEvents) {
        const previousGroup = groups[groups.length - 1]
        const nextSpanSeconds = previousGroup ? event.seconds - previousGroup.startSeconds : 0
        if (
          previousGroup &&
          (event.seconds - previousGroup.endSeconds) <= thresholdSeconds &&
          nextSpanSeconds <= maxGroupSpanSeconds
        ) {
          previousGroup.events.push(event)
          previousGroup.endSeconds = event.seconds
          previousGroup.startSeconds = Math.min(previousGroup.startSeconds, event.seconds)
          continue
        }

        groups.push({
          id: `group-${event.id}`,
          startSeconds: event.seconds,
          endSeconds: event.seconds,
          events: [event],
        })
      }

      return groups.map(group => {
        const isActive = group.events.some(event => event.id === this.selectedEventId)
        const isSelected = group.id === this.selectedTimelineGroupId
        const label = group.events.length === 1
          ? group.events[0].title
          : `${group.events[0].title} +${group.events.length - 1} more`
        const centerSeconds = (group.startSeconds + group.endSeconds) / 2
        return {
          ...group,
          centerSeconds,
          positionPct: Math.min(100, Math.max(0, (centerSeconds / duration) * 100)),
          isActive,
          isSelected,
          tooltip: `${this.formatDuration(group.startSeconds)} • ${label}`,
        }
      })
    },
    selectedTimelineGroup() {
      return this.clusteredTimelineMarkers.find(group => group.id === this.selectedTimelineGroupId) || null
    },
    expandedTimelineRange() {
      const group = this.selectedTimelineGroup
      if (!group) {
        return { startSeconds: 0, endSeconds: 0, durationSeconds: 1 }
      }

      const totalDuration = this.timelineDurationSeconds || 0
      if (group.events.length === 1) {
        const center = group.events[0].seconds
        const halfWindow = 45
        let startSeconds = Math.max(0, center - halfWindow)
        let endSeconds = Math.min(totalDuration || center + halfWindow, center + halfWindow)

        if ((endSeconds - startSeconds) < (halfWindow * 2)) {
          startSeconds = Math.max(0, endSeconds - (halfWindow * 2))
          endSeconds = startSeconds + (halfWindow * 2)
        }

        const durationSeconds = Math.max(1, endSeconds - startSeconds)
        return {
          startSeconds,
          endSeconds,
          durationSeconds,
        }
      }

      if (group.events.length === 2) {
        const center = (group.events[0].seconds + group.events[1].seconds) / 2
        const eventSpan = Math.max(1, group.endSeconds - group.startSeconds)
        const halfWindow = Math.max(18, Math.min(40, (eventSpan / 2) + 14))
        let startSeconds = Math.max(0, center - halfWindow)
        let endSeconds = Math.min(totalDuration || (center + halfWindow), center + halfWindow)

        if ((endSeconds - startSeconds) < (halfWindow * 2)) {
          startSeconds = Math.max(0, endSeconds - (halfWindow * 2))
          endSeconds = Math.min(totalDuration || (startSeconds + (halfWindow * 2)), startSeconds + (halfWindow * 2))
        }

        const durationSeconds = Math.max(1, endSeconds - startSeconds)
        return {
          startSeconds,
          endSeconds,
          durationSeconds,
        }
      }

      const paddingSeconds = 10
      const startSeconds = Math.max(0, group.startSeconds - paddingSeconds)
      const endSeconds = Math.min(
        totalDuration || (group.endSeconds + paddingSeconds),
        Math.max(startSeconds + 1, group.endSeconds + paddingSeconds)
      )
      const durationSeconds = endSeconds - startSeconds
      return {
        startSeconds,
        endSeconds,
        durationSeconds,
      }
    },
    expandedTimelineMarkers() {
      const group = this.selectedTimelineGroup
      if (!group) return []

      const rangeStart = Number(this.expandedTimelineRange?.startSeconds || 0)
      const rangeDuration = Math.max(1, Number(this.expandedTimelineRange?.durationSeconds || 1))
      const laneCounts = {}
      return group.events.map(event => {
        const laneKey = String(event.seconds)
        const laneIndex = laneCounts[laneKey] || 0
        laneCounts[laneKey] = laneIndex + 1
        const relativeSeconds = event.seconds - rangeStart
        const positionPct = Math.min(100, Math.max(0, (relativeSeconds / rangeDuration) * 100))
        return {
          ...event,
          laneIndex,
          positionPct,
          topPx: 38 + (laneIndex * 28),
        }
      })
    },
    expandedTimelineHeightPx() {
      const laneCount = this.expandedTimelineMarkers.reduce((max, event) => Math.max(max, (event.laneIndex || 0) + 1), 1)
      return 92 + ((laneCount - 1) * 28)
    },
    videoOptions() {
      return {
        autoplay: false,
        controls: true,
        preload: 'auto',
        fluid: false,
        responsive: false,
        width: 1920,
        height: 1080,
        sources: this.activeVideoUrl
          ? [
              {
                type: this.activeVideoType,
                src: this.activeVideoUrl,
              },
            ]
          : [],
      }
    },
  },
  methods: {
    newSearchId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID()
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`
    },
    trackProductEvent(activityType, metadata = {}) {
      return trackAdminEvent(activityType, {
        toolKey: 'ceremony_intelligence',
        metadata,
      })
    },
    resultTrackingMetadata(event) {
      const rank = this.eventsData.findIndex(row => row.id === event?.id)
      const startSeconds = Number(event?.cluster_start_sec ?? event?.windowStartSeconds ?? event?.timestamp)
      const endSeconds = Number(event?.cluster_end_sec ?? event?.windowEndSeconds)
      return {
        searchId: this.currentSearchId,
        ceremonyId: this.selectedCeremonyId,
        resultType: event?.type || 'scene',
        rank: rank >= 0 ? rank + 1 : null,
        startSeconds: Number.isFinite(startSeconds) ? Math.round(startSeconds) : null,
        endSeconds: Number.isFinite(endSeconds) ? Math.round(endSeconds) : null,
      }
    },
    async initialLoad() {
      await this.fetchUniversities()
      await this.onUniversityChange()
    },
    async loadEventsAPI() {
      if (this.eventsLoading) return

      this.eventsLoading = true
      this.eventsUploadError = ''
      this.clearEvents()
      const base = process.env.VUE_APP_BASE_URL || ''
      const queryUrl = base + '/semantic-search/query'
      const token = localStorage.getItem('token') || ''
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const payload = {
        ceremony_id: this.selectedCeremonyId,
        query: this.queryText,
        top_k: this.top_k,
      }
      const startedAt = Date.now()
      let outcome = 'success'
      let sceneCount = 0
      let transcriptCount = 0
      let rerankMetadata = {}

      try {
        const query = await axios.post(queryUrl, payload, {
          headers,
          timeout: 28000,
        })
        const eventData = query.data || {}
        rerankMetadata = eventData.multimodal_rerank || {}
        const rawEvents = Array.isArray(eventData.scenes) ? eventData.scenes.map((item, index) => ({ item, index, source: 'scenes' })) : []
        const transcriptEvents = Array.isArray(eventData.transcript_matches)
          ? eventData.transcript_matches.map((item, index) => ({ item, index, source: 'transcript' }))
          : []
        sceneCount = rawEvents.length
        transcriptCount = transcriptEvents.length
        const mappedEvents = rawEvents.concat(transcriptEvents)
          .map(({ item, index, source }) => (
            source === 'scenes'
              ? this.mapSceneToEvent(item, index)
              : this.mapTranscriptMatchToEvent(item, index)
          ))
          .filter(Boolean)

        if (!mappedEvents.length) {
          outcome = 'empty'
          this.clearEvents()
          this.eventsUploadError = 'No matching scenes were returned.'
          return
        }

        this.eventsData = mappedEvents
        this.selectedEventId = mappedEvents[0].id
        this.selectedTimelineGroupId = ''
        this.clearEventPlaybackTimer()
      } catch (error) {
        const timedOut = error?.code === 'ECONNABORTED' || error?.response?.status === 504
        outcome = timedOut
          ? 'timeout'
          : (error?.response?.status === 409 ? 'not_ready' : 'error')
        this.eventsUploadError = timedOut
          ? 'Search is taking longer than expected. Please try again; no work was lost.'
          : error?.response?.data?.message || error?.response?.data?.error || 'Failed to load events.'
      } finally {
        this.eventsLoading = false
        this.trackProductEvent('semantic_search_completed', {
          searchId: this.currentSearchId,
          ceremonyId: this.selectedCeremonyId,
          universitySlug: this.selectedSlug,
          outcome,
          latencyMs: Date.now() - startedAt,
          sceneCount,
          transcriptCount,
          rerankStatus: rerankMetadata.status || null,
          rerankModelId: rerankMetadata.model_id || null,
          rerankDurationMs: rerankMetadata.duration_ms ?? null,
          rerankCandidateCount: rerankMetadata.candidate_count ?? null,
          rerankRejectedCount: rerankMetadata.rejected_count ?? null,
          rerankErrorCount: rerankMetadata.error_count ?? null,
          rerankOrderChanged: rerankMetadata.order_changed ?? null,
          rerankTopResultChanged: rerankMetadata.top_result_changed ?? null,
        })
      }
    },
    async fetchUniversities() {
      try {
        const response = await axios.get('/universities')
        const rows = Array.isArray(response.data) ? response.data : []
        const allowed = new Set(this.tokenUniversities)
        this.allowedUniversities = this.isAdmin
          ? rows
          : rows.filter(u => allowed.has(u.slug))
      } catch (_) {
        this.allowedUniversities = (this.tokenUniversities || []).map(slug => ({ slug, name: slug }))
      }

      if (!this.selectedSlug && this.allowedUniversities.length) {
        this.selectedSlug = this.allowedUniversities[0].slug
      }
    },
    async onUniversityChange() {
      this.clearContextPolling()
      this.clearExportPolling()
      this.eventExports = {}
      this.selectedCeremonyId = ''
      this.hasSubmittedSearch = false
      this.ceremonyHighlights = []
      this.ceremonyHighlightsLoading = false
      this.ceremonyChapters = []
      this.contextMessage = ''
      this.contextError = ''
      this.clearCeremonyVideo()
      await this.fetchCeremonies()
      if (this.selectedCeremonyId) {
        await this.onCeremonyChange()
      }
    },
    async onCeremonyChange() {
      this.clearContextPolling()
      this.clearExportPolling()
      this.eventExports = {}
      this.hasSubmittedSearch = false
      this.ceremonyHighlights = []
      this.ceremonyHighlightsLoading = false
      this.ceremonyChapters = []
      this.contextMessage = ''
      this.contextError = ''
      this.clearEvents()
      this.eventsUploadError = ''
      if (this.selectedCeremonyId) {
        this.trackProductEvent('ceremony_selected', {
          ceremonyId: this.selectedCeremonyId,
          universitySlug: this.selectedSlug,
        })
      }
      await this.fetchCeremonyHighlights()
    },
    setEventExportState(eventId, state) {
      this.eventExports = {
        ...this.eventExports,
        [eventId]: state,
      }
    },
    clearExportPolling(eventId = '') {
      if (eventId) {
        if (this.exportPollTimers[eventId]) {
          clearTimeout(this.exportPollTimers[eventId].timer)
          const remainingTimers = { ...this.exportPollTimers }
          delete remainingTimers[eventId]
          this.exportPollTimers = remainingTimers
        }
        return
      }

      Object.values(this.exportPollTimers).forEach(poll => clearTimeout(poll.timer))
      this.exportPollTimers = {}
    },
    scheduleExportPoll(eventId, exportId) {
      const existingPoll = this.exportPollTimers[eventId]
      if (existingPoll?.timer) clearTimeout(existingPoll.timer)
      const startedAt = existingPoll?.startedAt || Date.now()
      if (Date.now() - startedAt >= SECTION_EXPORT_POLL_DEADLINE_MS) {
        this.clearExportPolling(eventId)
        this.setEventExportState(eventId, {
          ...this.eventExports[eventId],
          status: 'CHECK_LATER',
          export_id: exportId,
          error: (
            'This export is taking longer than expected. '
            + 'You can check its status again without starting another export.'
          ),
        })
        return
      }
      const delay = existingPoll
        ? Math.min(existingPoll.delay * 2, SECTION_EXPORT_POLL_MAX_MS)
        : SECTION_EXPORT_POLL_INITIAL_MS
      const timer = setTimeout(() => {
        this.pollEventExport(eventId, exportId)
      }, delay)
      this.exportPollTimers = {
        ...this.exportPollTimers,
        [eventId]: { timer, startedAt, delay },
      }
    },
    applyEventExportResponse(eventId, data) {
      const previousStatus = String(this.eventExports[eventId]?.status || '').toUpperCase()
      const exportState = { ...(data || {}) }
      this.setEventExportState(eventId, exportState)
      const nextStatus = String(exportState.status || '').toUpperCase()
      if (
        ['COMPLETE', 'ERROR', 'CANCELED'].includes(nextStatus)
        && previousStatus !== nextStatus
      ) {
        const event = this.eventsData.find(row => row.id === eventId)
        this.trackProductEvent('section_export_completed', {
          ...this.resultTrackingMetadata(event),
          outcome: nextStatus.toLowerCase(),
        })
      }
      if (isSectionExportPending(exportState) && exportState.export_id) {
        this.scheduleExportPoll(eventId, exportState.export_id)
      } else {
        this.clearExportPolling(eventId)
      }
    },
    async requestEventExport({ eventId, event }) {
      if (!eventId || !event || isSectionExportPending(this.eventExports[eventId])) return

      this.trackProductEvent('section_export_requested', {
        ...this.resultTrackingMetadata(event),
        durationSeconds: Math.round(
          Number(event.cluster_end_sec ?? event.windowEndSeconds)
          - Number(event.cluster_start_sec ?? event.windowStartSeconds ?? event.timestamp)
        ),
      })
      this.setEventExportState(eventId, {
        status: 'SUBMITTING',
      })
      try {
        const result = await createSectionExport(this.selectedCeremonyId, {
          ...event,
          id: eventId,
        })
        this.applyEventExportResponse(eventId, result.data)
      } catch (error) {
        this.applyEventExportResponse(eventId, {
          status: 'ERROR',
          error: (
            error?.response?.data?.error
            || error?.message
            || 'The section export could not be started.'
          ),
        })
      }
    },
    async pollEventExport(eventId, exportId) {
      if (!eventId || !exportId) return
      try {
        const result = await getSectionExport(exportId)
        this.applyEventExportResponse(eventId, result.data)
      } catch (error) {
        if (error?.response?.status === 503) {
          this.scheduleExportPoll(eventId, exportId)
          return
        }
        this.applyEventExportResponse(eventId, {
          status: 'ERROR',
          export_id: exportId,
          error: error?.response?.data?.error || 'The section export status could not be loaded.',
        })
      }
    },
    recordExportDownload({ eventId }) {
      const event = this.eventsData.find(row => row.id === eventId)
      this.trackProductEvent('section_export_downloaded', {
        ...this.resultTrackingMetadata(event),
      })
    },
    async fetchCeremonyHighlights() {
      const ceremonyId = this.selectedCeremonyId
      const requestId = ++this.ceremonyContextRequestId
      this.ceremonyHighlights = []
      this.ceremonyChapters = []
      this.clearCeremonyVideo()
      this.ceremonyVideoLoading = true
      this.ceremonyHighlightsLoading = true
      try {
        const base = process.env.VUE_APP_BASE_URL || ''
        const queryUrl = base + `/semantic-search/highlights/${ceremonyId}`
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const response = await axios.get(queryUrl, { headers })
        if (
          requestId !== this.ceremonyContextRequestId
          || ceremonyId !== this.selectedCeremonyId
        ) return
        if (response.status === 200) {
          const highlights = Array.isArray(response.data.highlights) ? response.data.highlights : []
          const chapters = Array.isArray(response.data.chapters) ? response.data.chapters : []
          const preview = response.data.preview || {}
          this.ceremonyHighlights = highlights
          this.ceremonyChapters = chapters
          this.ceremonyVideoUrl = String(preview.url || '')
          this.ceremonyVideoType = String(preview.type || '')
          if (!this.ceremonyVideoUrl) {
            this.ceremonyVideoError = 'No indexed ceremony video is available.'
          }
        }
      } catch (error) {
        if (
          requestId !== this.ceremonyContextRequestId
          || ceremonyId !== this.selectedCeremonyId
        ) return
        this.ceremonyVideoError = (
          error?.response?.data?.error
          || 'Failed to load indexed ceremony context.'
        )
      } finally {
        if (
          requestId === this.ceremonyContextRequestId
          && ceremonyId === this.selectedCeremonyId
        ) {
          this.ceremonyHighlightsLoading = false
          this.ceremonyVideoLoading = false
        }
      }
    },
    async requestChapterBackfill() {
      if (!this.canBuildChapters || this.contextProcessing) return
      this.contextProcessing = true
      this.contextMessage = ''
      this.contextError = ''
      try {
        const base = process.env.VUE_APP_BASE_URL || ''
        const url = base + `/semantic-search/index-jobs/${this.selectedCeremonyId}/context`
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        await axios.post(url, {}, { headers })
        this.contextMessage = this.ceremonyChapters.length
          ? 'Chapter rebuild started…'
          : 'Chaptering started…'
        this.trackProductEvent('ceremony_chaptering_requested', {
          ceremonyId: this.selectedCeremonyId,
          universitySlug: this.selectedSlug,
        })
        this.contextPollDeadline = Date.now() + (15 * 60 * 1000)
        this.scheduleChapterStatusPoll(this.selectedCeremonyId)
      } catch (error) {
        this.contextError = (
          error?.response?.data?.error
          || 'Chaptering could not be started.'
        )
      } finally {
        this.contextProcessing = false
      }
    },
    clearContextPolling() {
      if (this.contextPollTimer) {
        clearTimeout(this.contextPollTimer)
        this.contextPollTimer = null
      }
      this.contextPollDeadline = 0
    },
    scheduleChapterStatusPoll(ceremonyId) {
      if (!ceremonyId || Date.now() >= this.contextPollDeadline) {
        this.contextMessage = 'Chaptering is still running. Check this ceremony again later.'
        this.clearContextPolling()
        return
      }
      this.contextPollTimer = setTimeout(
        () => this.pollChapterStatus(ceremonyId),
        5000
      )
    },
    async pollChapterStatus(ceremonyId) {
      if (ceremonyId !== this.selectedCeremonyId) {
        this.clearContextPolling()
        return
      }
      try {
        const base = process.env.VUE_APP_BASE_URL || ''
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const response = await axios.get(
          base + `/semantic-search/index-jobs/${ceremonyId}`,
          { headers }
        )
        const job = response.data?.job || {}
        const status = String(job.transcript_embedding_status || '').toUpperCase()
        if (status === 'SUCCEEDED') {
          this.contextMessage = 'Chapters are ready.'
          this.clearContextPolling()
          await this.fetchCeremonyHighlights()
          return
        }
        if (status === 'EMPTY') {
          this.contextMessage = 'No timed transcript sections were available.'
          this.clearContextPolling()
          return
        }
        if (status === 'FAILED' || status === 'SKIPPED') {
          this.contextMessage = ''
          this.contextError = job.transcript_embedding_reason || 'Chaptering failed.'
          this.clearContextPolling()
          return
        }
      } catch (_) {
        // A transient status failure should not start duplicate chaptering work.
      }
      this.scheduleChapterStatusPoll(ceremonyId)
    },
    async fetchCeremonies() {
      this.ceremoniesLoading = true
      this.ceremonyError = ''
      this.ceremonies = []

      const universityId = this.selectedUniversity?.id
      if (!universityId) {
        this.ceremoniesLoading = false
        return
      }

      try {
        const response = await axios.get(`/universities/${universityId}/ceremonies`)
        const rows = Array.isArray(response.data) ? response.data : []
        this.ceremonies = rows.slice().sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
        if (this.ceremonies.length) {
          this.selectedCeremonyId = String(this.ceremonies[0].id)
        }
      } catch (_) {
        this.ceremonyError = 'Failed to load ceremonies for the selected university.'
      } finally {
        this.ceremoniesLoading = false
      }
    },
    clearCeremonyVideo() {
      this.ceremonyVideoLoading = false
      this.ceremonyVideoError = ''
      this.ceremonyVideoUrl = ''
      this.ceremonyVideoType = ''
      this.ceremonyVideoDuration = 0
    },
    clearEvents() {
      this.eventsData = []
      this.selectedEventId = ''
      this.selectedTimelineGroupId = ''
      this.clearEventPlaybackTimer()
    },
    onPlayerLoadedData() {
      const duration = this.$refs.mediaPlayer?.player?.duration?.()
      if (Number.isFinite(duration) && duration > 0) {
        this.ceremonyVideoDuration = duration
      }
    },
    mapSceneToEvent(scene, index) {
      const timestamp = Number(
        scene?.best_frame_timestamp_sec ??
        scene?.best_match_timestamp_sec ??
        scene?.best_match_start_sec ??
        scene?.cluster_start_sec
      )
      if (!Number.isFinite(timestamp)) return null

      const windowStart = Number(
        scene?.best_window_start_sec ??
        scene?.best_match_start_sec ??
        scene?.cluster_start_sec ??
        timestamp
      )
      const windowEnd = Number(
        scene?.best_window_end_sec ??
        scene?.best_match_end_sec ??
        scene?.cluster_end_sec ??
        timestamp
      )
      const durationSeconds = Math.max(1, windowEnd - windowStart)
      const sceneScore = Number(scene?.scene_score ?? scene?.relevance_score ?? scene?.best_score)
      const relativeScore = Number(scene?.relative_relevance_score)
      const peakScore = Number(scene?.peak_score ?? scene?.best_score)
      const supportScore = Number(scene?.support_score)
      const tightnessScore = Number(scene?.tightness_score)
      const distance = Number(scene?.best_distance)
      const anchorHits = Number(scene?.anchor_hit_count)
      const supportingHits = Number(scene?.supporting_hits)
      const sourceUploadId = String(scene?.source_upload_id || '').trim()
      const ceremonyId = String(scene?.ceremony_id || '').trim()
      const eventId = String(scene?.best_key || '').trim() || `scene-event-${index + 1}`
      const chapterLabel = String(scene?.chapter?.label || '').split('_').join(' ').trim()
      const chapterSummary = String(scene?.chapter?.summary || '').trim()

      return {
        id: eventId,
        best_key: eventId,
        title: chapterSummary || `Scene ${index + 1}`,
        timestamp,
        windowStartSeconds: windowStart,
        windowEndSeconds: windowEnd,
        cluster_start_sec: Number.isFinite(Number(scene?.cluster_start_sec)) ? Number(scene.cluster_start_sec) : windowStart,
        cluster_end_sec: Number.isFinite(Number(scene?.cluster_end_sec)) ? Number(scene.cluster_end_sec) : windowEnd,
        artifacts_keys: Array.isArray(scene?.artifacts_keys)
          ? scene.artifacts_keys
          : (Array.isArray(scene?.artifact_keys) ? scene.artifact_keys : []),
        relative_relevance_score: relativeScore,
        type: 'frame',
        score: Number.isFinite(sceneScore) ? sceneScore.toFixed(3) : null,
        distance: Number.isFinite(distance) ? distance : null,
        duration: this.formatDuration(durationSeconds),
        headline: `Start ${this.formatDuration(windowStart)} Duration ${this.formatDuration(durationSeconds)}`,
        summary: `Cluster ${this.formatDuration(windowStart)} to ${this.formatDuration(windowEnd)} • Peak ${this.formatDuration(timestamp)}`,
        description: chapterSummary
          || `Scene cluster match for ceremony ${ceremonyId || 'unknown'}${sourceUploadId ? ` from upload ${sourceUploadId}` : ''}.`,
        metadata: `Score ${Number.isFinite(sceneScore) ? sceneScore.toFixed(3) : 'n/a'} • Relative ${Number.isFinite(relativeScore) ? relativeScore.toFixed(3) : 'n/a'} • Anchor hits ${Number.isFinite(anchorHits) ? anchorHits : 'n/a'} • Supporting hits ${Number.isFinite(supportingHits) ? supportingHits : 'n/a'}`,
        tags: [
          'frame',
          ...(chapterLabel ? [chapterLabel] : []),
          Number.isFinite(peakScore) ? `peak ${peakScore.toFixed(3)}` : 'peak n/a',
          Number.isFinite(supportScore) ? `support ${supportScore.toFixed(3)}` : 'support n/a',
          Number.isFinite(tightnessScore) ? `tightness ${tightnessScore.toFixed(3)}` : 'tightness n/a',
        ],
      }
    },
    chapterTitle(chapter) {
      const summary = String(chapter?.summary || '').trim()
      if (summary) return summary
      return String(chapter?.label || 'Ceremony section').split('_').join(' ')
    },
    chapterEventId(chapter, index) {
      return `chapter-${index}-${Number(chapter?.start || 0)}`
    },
    mapChapterToEvent(chapter, index) {
      const start = Number(chapter?.start)
      const end = Number(chapter?.end)
      if (!Number.isFinite(start)) return null
      const safeEnd = Number.isFinite(end) && end > start ? end : start + 1
      const label = String(chapter?.label || 'section').split('_').join(' ')
      const title = this.chapterTitle(chapter)
      return {
        id: this.chapterEventId(chapter, index),
        title,
        timestamp: start,
        windowStartSeconds: start,
        windowEndSeconds: safeEnd,
        cluster_start_sec: start,
        cluster_end_sec: safeEnd,
        type: 'chapter',
        duration: this.formatDuration(safeEnd - start),
        headline: title,
        summary: `${label} • ${this.formatDuration(start)} to ${this.formatDuration(safeEnd)}`,
        description: `Transcript-derived ${label} chapter.`,
        tags: ['chapter', label],
      }
    },
    chapterEventFromId(eventId) {
      if (!String(eventId || '').startsWith('chapter-')) return null
      const index = this.ceremonyChapters.findIndex(
        (chapter, chapterIndex) => this.chapterEventId(chapter, chapterIndex) === eventId
      )
      return index >= 0 ? this.mapChapterToEvent(this.ceremonyChapters[index], index) : null
    },
    async selectChapter(chapter, index) {
      const event = this.mapChapterToEvent(chapter, index)
      if (!event) return
      this.selectedEventId = event.id
      this.selectedTimelineGroupId = ''
      this.trackProductEvent('ceremony_chapter_selected', {
        ...this.resultTrackingMetadata(event),
      })
      await this.$nextTick()
      this.playEventPreview(event)
    },
    mapTranscriptMatchToEvent(match, index) {
      const start = Number(match?.start_second)
      const end = Number(match?.end_second)
      if (!Number.isFinite(start)) return null
      const safeEnd = Number.isFinite(end) && end > start ? end : start + 1
      const score = Number(match?.score)
      const chapterSummary = String(match?.chapter?.summary || '').trim()
      const chapterLabel = String(match?.chapter?.label || 'transcript').split('_').join(' ')
      const text = String(match?.text || '').trim()
      const eventId = String(match?.key || '').trim() || `transcript-event-${index + 1}`

      return {
        id: eventId,
        best_key: eventId,
        title: chapterSummary || `Transcript ${index + 1}`,
        timestamp: start,
        windowStartSeconds: start,
        windowEndSeconds: safeEnd,
        cluster_start_sec: start,
        cluster_end_sec: safeEnd,
        artifacts_keys: [],
        relative_relevance_score: score,
        type: 'transcript',
        score: Number.isFinite(score) ? score.toFixed(3) : null,
        distance: Number.isFinite(Number(match?.distance)) ? Number(match.distance) : null,
        duration: this.formatDuration(safeEnd - start),
        headline: text || chapterSummary || 'Transcript match',
        summary: `${chapterLabel} • ${this.formatDuration(start)} to ${this.formatDuration(safeEnd)}`,
        description: text || 'Transcript match for this ceremony.',
        metadata: `Transcript score ${Number.isFinite(score) ? score.toFixed(3) : 'n/a'}`,
        tags: ['transcript', chapterLabel],
      }
    },
    highlightEventId(highlight) {
      const sceneId = String(
        highlight?.scene_id ??
        highlight?.best_key ??
        highlight?.id ??
        ''
      ).trim()
      if (sceneId) return `highlight-${sceneId}`

      const timestamp = Number(
        highlight?.best_frame_timestamp_sec ??
        highlight?.best_match_timestamp_sec ??
        highlight?.start_second
      )
      const sourceHighlightId = String(highlight?.highlight_id || '').trim() || 'highlight'
      return Number.isFinite(timestamp) ? `highlight-${sourceHighlightId}-${timestamp}` : ''
    },
    mapHighlightToEvent(highlight) {
      const timestamp = Number(highlight?.best_frame_timestamp_sec ?? highlight?.start_second)
      const windowStart = Number(highlight?.start_second ?? timestamp)
      const windowEnd = Number(highlight?.end_second ?? timestamp)
      const durationSeconds = Number(highlight?.duration_seconds) > 0
        ? Number(highlight?.duration_seconds)
        : Math.max(1, windowEnd - windowStart)
      const relevanceScore = Number(highlight?.relevance_score)
      const confidenceScore = Number(highlight?.confidence_score)
      const supportingHits = Number(highlight?.supporting_hits)
      const supportingQueryCount = Number(highlight?.supporting_query_count)

      if (!Number.isFinite(timestamp)) return null

      return {
        id: this.highlightEventId(highlight),
        best_key: this.highlightEventId(highlight),
        title: String(highlight?.label || highlight?.highlight_label || 'Highlight'),
        timestamp,
        windowStartSeconds: Number.isFinite(windowStart) ? windowStart : timestamp,
        windowEndSeconds: Number.isFinite(windowEnd) ? windowEnd : timestamp + durationSeconds,
        cluster_start_sec: Number.isFinite(windowStart) ? windowStart : timestamp,
        cluster_end_sec: Number.isFinite(windowEnd) ? windowEnd : timestamp + durationSeconds,
        artifacts_keys: Array.isArray(highlight?.artifact_keys) ? highlight.artifact_keys : [],
        relative_relevance_score: Number.isFinite(confidenceScore) ? confidenceScore : relevanceScore,
        type: 'highlight',
        score: Number.isFinite(relevanceScore) ? relevanceScore.toFixed(3) : null,
        distance: null,
        duration: this.formatDuration(durationSeconds),
        headline: `Start ${this.formatDuration(windowStart)} • Duration ${this.formatDuration(durationSeconds)}`,
        summary: `Peak ${this.formatDuration(timestamp)} • Duration ${this.formatDuration(durationSeconds)}`,
        description: `Curated highlight for this ceremony based on grouped semantic search matches.`,
        metadata: `Confidence ${Number.isFinite(confidenceScore) ? confidenceScore.toFixed(3) : 'n/a'} • Relevance ${Number.isFinite(relevanceScore) ? relevanceScore.toFixed(3) : 'n/a'} • Supporting hits ${Number.isFinite(supportingHits) ? supportingHits : 'n/a'} • Supporting queries ${Number.isFinite(supportingQueryCount) ? supportingQueryCount : 'n/a'}`,
        tags: [
          'highlight',
          Number.isFinite(confidenceScore) ? `confidence ${confidenceScore.toFixed(3)}` : 'confidence n/a',
          Number.isFinite(relevanceScore) ? `relevance ${relevanceScore.toFixed(3)}` : 'relevance n/a',
        ],
      }
    },
    highlightEventFromId(eventId) {
      if (!String(eventId || '').startsWith('highlight-')) return null
      const highlight = this.ceremonyHighlights.find(item => this.highlightEventId(item) === eventId)
      return highlight ? this.mapHighlightToEvent(highlight) : null
    },
    parseDurationToSeconds(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value
      if (!value || typeof value !== 'string') return 0
      const parts = value.split(':').map(part => Number(part))
      if (parts.some(part => !Number.isFinite(part))) return 0
      if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2]
      if (parts.length === 2) return (parts[0] * 60) + parts[1]
      return parts[0]
    },
    clearEventPlaybackTimer() {
      if (this.eventPlaybackTimer) {
        clearTimeout(this.eventPlaybackTimer)
        this.eventPlaybackTimer = null
      }
    },
    getSafeSeekTime(player, requestedSeconds) {
      const requested = Number(requestedSeconds)
      if (!Number.isFinite(requested)) return null

      const seekable = player?.seekable?.()
      if (seekable && seekable.length) {
        const rangeStart = Number(seekable.start(0))
        const rangeEnd = Number(seekable.end(seekable.length - 1))
        if (Number.isFinite(rangeStart) && Number.isFinite(rangeEnd) && rangeEnd > rangeStart) {
          const bufferedEnd = Math.max(rangeStart, rangeEnd - 0.5)
          return Math.min(Math.max(requested, rangeStart), bufferedEnd)
        }
      }

      const duration = Number(player?.duration?.())
      if (Number.isFinite(duration) && duration > 0) {
        return Math.min(Math.max(requested, 0), Math.max(0, duration - 0.5))
      }

      return Math.max(requested, 0)
    },
    playEventPreview(event) {
      const player = this.$refs.mediaPlayer?.player
      if (!player || !event) return

      const requestedStartSeconds = Number(event.windowStartSeconds ?? event.timestamp)
      const durationSeconds = this.parseDurationToSeconds(event.duration)
      const startSeconds = this.getSafeSeekTime(player, requestedStartSeconds)
      if (!Number.isFinite(startSeconds)) return

      this.trackProductEvent('preview_started', {
        ...this.resultTrackingMetadata(event),
      })
      this.clearEventPlaybackTimer()
      player.pause()

      const beginPlayback = () => {
        const playPromise = player.play()
        if (playPromise?.catch) {
          playPromise.catch(() => {})
        }
      }

      player.one('seeked', beginPlayback)
      player.currentTime(startSeconds)

      if (durationSeconds > 0) {
        this.eventPlaybackTimer = setTimeout(() => {
          player.pause()
          this.eventPlaybackTimer = null
        }, durationSeconds * 1000)
      }
    },
    selectEvent(eventId, preserveFocusedGroup = false) {
      const event = this.eventsData.find(row => row.id === eventId)
      if (!event) return
      this.trackProductEvent('search_result_selected', {
        ...this.resultTrackingMetadata(event),
      })
      this.selectedEventId = event.id
      if (!preserveFocusedGroup) {
        this.selectedTimelineGroupId = ''
      }
      this.playEventPreview(event)
    },
    async selectHighlight(highlight) {
      const event = this.mapHighlightToEvent(highlight)
      if (!event) return
      this.trackProductEvent('search_result_selected', {
        ...this.resultTrackingMetadata(event),
      })
      this.selectedEventId = event.id
      this.selectedTimelineGroupId = ''
      await this.$nextTick()
      this.playEventPreview(event)
    },
    selectFocusedTimelineEvent(eventId) {
      this.selectEvent(eventId, true)
    },
    formatCeremony(ceremony) {
      if (!ceremony) return 'Unknown ceremony'
      const datetime = ceremony.datetime ? this.formatDateTime(ceremony.datetime) : 'No date'
      const venue = ceremony.venue ? ` • ${ceremony.venue}` : ''
      return `${datetime}${venue}`
    },
    formatDuration(totalSeconds) {
      const safeSeconds = Math.max(0, Math.round(totalSeconds || 0))
      const hours = Math.floor(safeSeconds / 3600)
      const minutes = Math.floor((safeSeconds % 3600) / 60)
      const seconds = safeSeconds % 60
      if (hours > 0) {
        return [hours, minutes, seconds].map((part, index) => String(part).padStart(index === 0 ? 1 : 2, '0')).join(':')
      }
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    selectTimelineMarker(group) {
      if (!group || !group.events?.length) return
      const shouldFocusGroup = group.events.length > 1
      this.selectedTimelineGroupId = shouldFocusGroup ? group.id : ''
      const selected = group.events.find(event => event.id === this.selectedEventId)
      this.selectEvent((selected || group.events[0]).id, shouldFocusGroup)
    },
    submitSearch() {
      if (this.eventsLoading) return
      if (!this.canSubmitSearch) return
      this.currentSearchId = this.newSearchId()
      this.trackProductEvent('semantic_search_submitted', {
        searchId: this.currentSearchId,
        ceremonyId: this.selectedCeremonyId,
        universitySlug: this.selectedSlug,
        queryLength: String(this.queryText || '').length,
        topK: this.top_k,
      })
      this.hasSubmittedSearch = true
      this.loadEventsAPI()
    },
    async recordEventFeedback({ eventId, response }) {
      if (!eventId || !response) return
      if (this.eventFeedback[eventId] || this.feedbackSubmitting[eventId]) return
      const event = this.eventsData.find(row => row.id === eventId)
      this.feedbackSubmitting = {
        ...this.feedbackSubmitting,
        [eventId]: true,
      }
      this.feedbackErrors = {
        ...this.feedbackErrors,
        [eventId]: '',
      }
      const saved = await this.trackProductEvent('feedback_submitted', {
        ...this.resultTrackingMetadata(event),
        response,
        relativeRelevanceScore: event?.relative_relevance_score ?? null,
      })
      this.feedbackSubmitting = {
        ...this.feedbackSubmitting,
        [eventId]: false,
      }
      if (saved) {
        this.eventFeedback = {
          ...this.eventFeedback,
          [eventId]: { response },
        }
        return
      }
      this.feedbackErrors = {
        ...this.feedbackErrors,
        [eventId]: 'Feedback could not be saved. Please try again.',
      }
    },
  },
  created() {
    this.trackProductEvent('ceremony_intelligence_opened')
    this.initialLoad()
  },
  beforeDestroy() {
    this.clearEventPlaybackTimer()
    this.clearExportPolling()
    this.clearContextPolling()
  },
}
</script>

<style scoped>
.container {
  max-width: 1040px;
  margin: 0 auto;
  padding: 8px 12px 24px;
}

.panel {
  /* background: #ffffff; */
  /* border: 1px solid #d9dee7; */
  /* padding: 20px; */
  margin-bottom: 24px;
  /* box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05); */
}
</style>
